import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { Badge, Card, ErrorText, PageHeader, Spinner } from "../components/ui.tsx";
import { fmtUsd, UsageBar } from "../components/runtime.tsx";
import { ColumnChart, compact } from "../components/charts.tsx";

const num = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : String(n));

export function UsagePage() {
  const q = useQuery({ queryKey: ["usage"], queryFn: () => unwrap(api.admin.usage.$get()), refetchInterval: 30_000 });
  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const { byUser, byModel, daily, monthStart, system } = q.data;
  const systemSpend = system.reduce((a, s) => a + s.spendUsd, 0);
  const total = byUser.reduce((a, u) => a + u.spendUsd, 0) + systemSpend;
  const requests = byUser.reduce((a, u) => a + u.requests, 0);
  const blocked = byUser.reduce((a, u) => a + u.blocked, 0);
  const nearCap = byUser.filter((u) => u.budgetUsd !== null && u.budgetUsd > 0 && u.spendUsd / u.budgetUsd >= 0.8).length;
  const month = new Date(monthStart).toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <>
      <PageHeader title="Usage" description={`AI spend for ${month}. Budgets come from each user's group and reset on the 1st.`} />
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Spend this month" value={fmtUsd(total)} />
        <Kpi label="Requests" value={num(requests)} />
        <Kpi label="Users at ≥80% of budget" value={String(nearCap)} warn={nearCap > 0} />
        <Kpi label="Requests blocked by budget" value={String(blocked)} warn={blocked > 0} />
      </div>

      <Card className="mb-6 p-5">
        <h2 className="text-sm font-semibold">AI spend</h2>
        <p className="mb-5 text-xs text-[var(--muted)]">Last 30 days, everyone together</p>
        {daily.length === 0 ? (
          <p className="text-sm text-[var(--muted)]">No usage yet.</p>
        ) : (
          <ColumnChart
            label="AI spend per day, last 30 days"
            data={daily.map((d) => ({ key: d.day, label: new Date(`${d.day}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" }), value: d.spendUsd, detail: `${d.requests.toLocaleString()} requests` }))}
            format={fmtUsd}
            tick={(v) => `$${compact(v)}`}
          />
        )}
      </Card>

      <Card className="mb-6 overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-[var(--line)] bg-[var(--panel-2)]/40 text-left text-[12.5px] font-medium whitespace-nowrap text-[var(--muted)]">
              <th className="px-4 py-3 font-medium">User</th>
              <th className="px-4 py-3 font-medium">Budget used</th>
              <th className="px-4 py-3 font-medium">Requests</th>
              <th className="px-4 py-3 font-medium">Tokens in / out</th>
            </tr>
          </thead>
          <tbody>
            {byUser.map((u) => (
              <tr key={u.userId} className="border-b border-[var(--line)] last:border-0">
                <td className="px-4 py-3">
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs text-[var(--muted)]">{u.email} · {u.groupName ?? "No group"}</div>
                </td>
                <td className="w-64 px-4 py-3">
                  <UsageBar used={u.spendUsd} total={u.budgetUsd} label={u.budgetUsd === null ? `${fmtUsd(u.spendUsd)} · no limit` : `${fmtUsd(u.spendUsd)} of ${fmtUsd(u.budgetUsd)}`} />
                </td>
                <td className="px-4 py-3">
                  {num(u.requests)} {u.blocked > 0 && <Badge tone="bad">{u.blocked} blocked</Badge>}
                </td>
                <td className="px-4 py-3 text-[var(--muted)]">{num(u.tokensIn)} / {num(u.tokensOut)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {system.length > 0 && (
        <Card className="mb-6 p-5">
          <h2 className="mb-1 text-sm font-semibold">Company AI work</h2>
          <p className="mb-4 text-xs text-[var(--muted)]">Memory extraction, company memory, skill review and knowledge sync. Paid by the company, not from anyone's budget.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {system.map((s) => (
              <div key={s.principal} className="rounded-lg border border-[var(--line)] p-3 text-sm">
                <div className="font-medium">{({ promoter: "Company memory promotion", skills: "Skill sensitivity review", sync: "Knowledge sync agents" } as Record<string, string>)[s.principal] ?? "Memory extraction"}</div>
                <div className="mt-1 text-[var(--muted)]">
                  {fmtUsd(s.spendUsd)} · {num(s.requests)} requests · {num(s.tokensIn)} / {num(s.tokensOut)} tokens
                  {s.failures > 0 && <> · <Badge tone="bad">{s.failures} failed</Badge></>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="border-b border-[var(--line)] bg-[var(--panel-2)]/40 text-left text-[12.5px] font-medium whitespace-nowrap text-[var(--muted)]">
              <th className="px-4 py-3 font-medium">Model</th>
              <th className="px-4 py-3 font-medium">Spend</th>
              <th className="px-4 py-3 font-medium">Requests</th>
              <th className="px-4 py-3 font-medium">Failures</th>
              <th className="px-4 py-3 font-medium">Avg latency</th>
            </tr>
          </thead>
          <tbody>
            {byModel.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[var(--muted)]">No usage yet.</td></tr>}
            {byModel.map((m) => (
              <tr key={m.model} className="border-b border-[var(--line)] last:border-0">
                <td className="px-4 py-3 font-mono text-xs">{m.model}</td>
                <td className="px-4 py-3">{fmtUsd(m.spendUsd)}</td>
                <td className="px-4 py-3">{num(m.requests)}</td>
                <td className="px-4 py-3">{m.failures ? <Badge tone="bad">{m.failures}</Badge> : <span className="text-[var(--muted)]">0</span>}</td>
                <td className="px-4 py-3 text-[var(--muted)]">{(m.avgLatencyMs / 1000).toFixed(1)} s</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

function Kpi({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <Card className="p-4">
      <div className="text-[12.5px] text-[var(--muted)]">{label}</div>
      <div className={`mt-2 text-[24px] leading-none font-semibold tracking-[-0.02em] ${warn ? "text-amber-700" : ""}`}>{value}</div>
    </Card>
  );
}
